// src/hooks/useOfflineDraft.js
// Sauvegarde les brouillons de journal en localStorage
// Sync vers Firestore automatiquement au retour du réseau

import { useState, useEffect, useCallback } from "react";

const DRAFT_KEY  = "takkyu_journal_draft";
const QUEUE_KEY  = "takkyu_sync_queue";

// ── Draft unique (texte en cours de saisie) ────────────────────────────
export function useJournalDraft() {
  const [draft, setDraftState] = useState(() => {
    try { return localStorage.getItem(DRAFT_KEY) || ""; }
    catch { return ""; }
  });

  const setDraft = useCallback((text) => {
    setDraftState(text);
    try { localStorage.setItem(DRAFT_KEY, text); }
    catch(e) { console.warn("[Draft] localStorage plein:", e.message); }
  }, []);

  const clearDraft = useCallback(() => {
    setDraftState("");
    try { localStorage.removeItem(DRAFT_KEY); }
    catch {}
  }, []);

  return { draft, setDraft, clearDraft };
}

// ── Queue de sync (entrées en attente d'envoi) ─────────────────────────
export function useSyncQueue() {
  const [queue, setQueueState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); }
    catch { return []; }
  });
  const [syncing, setSyncing] = useState(false);

  const saveQueue = (q) => {
    setQueueState(q);
    try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); }
    catch {}
  };

  const enqueue = useCallback((entry) => {
    const q = (() => {
      try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); }
      catch { return []; }
    })();
    const newQ = [...q, { ...entry, queuedAt: new Date().toISOString(), id: `draft_${Date.now()}` }];
    saveQueue(newQ);
    return newQ;
  }, []);

  const dequeue = useCallback((ids) => {
    const q = (() => {
      try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); }
      catch { return []; }
    })();
    saveQueue(q.filter(e => !ids.includes(e.id)));
  }, []);

  const syncToFirestore = useCallback(async (saveFn) => {
    const q = (() => {
      try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); }
      catch { return []; }
    })();
    if (!q.length || syncing) return;
    setSyncing(true);
    const synced = [];
    for (const entry of q) {
      try {
        await saveFn(entry);
        synced.push(entry.id);
      } catch(e) {
        console.error("[Sync] Échec pour", entry.id, e.message);
        break;
      }
    }
    if (synced.length) dequeue(synced);
    setSyncing(false);
    return synced.length;
  }, [syncing, dequeue]);

  return { queue, enqueue, dequeue, syncToFirestore, syncing };
}

// ── Hook principal : online/offline detection ──────────────────────────
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online",  goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return isOnline;
}

// ── Service Worker registration ────────────────────────────────────────
// DESACTIVE EN DEV — decommenter pour la prod (build optimise)
// Le SW cache agressivement le bundle et empeche de voir les changements
// sans hard-refresh pendant le developpement.
export function registerServiceWorker() {
  if (process.env.NODE_ENV !== "production") {
    return; // pas de SW en dev
  }
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/sw.js")
        .then(reg => {
          console.log("[SW] Enregistré:", reg.scope);
          navigator.serviceWorker.addEventListener("message", (event) => {
            if (event.data?.type === "SYNC_JOURNAL") {
              window.dispatchEvent(new CustomEvent("takkyu:sync-journal"));
            }
          });
        })
        .catch(err => console.warn("[SW] Erreur:", err));
    });
  }
}
